import { useId, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyFeedback } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import type { FeedbackBundle } from "@/lib/types";
import { useScopeIdentity } from '@/features/navigation/useScopeSelection';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

/**
 * The `/api/my-feedback` bundle hook (M7) — one round-trip feeding
 * every `/feedback` list tab.
 *
 * Scope-keyed: the backend resolves the viewer's team from the request
 * scope (`apiFetch` auto-injects `team_id` from the scope store), so
 * multi-team viewers must not reuse another active team's cached bundle.
 * Include both active team and season in the query key so scope changes
 * remount the feedback bundle in a pending state instead of showing stale
 * notes/reviews from the previous team while the refetch is in flight.
 *
 * The shell can disable the query after `/api/me` proves the signed-in
 * role should not use the player/guardian feedback surface.
 */
export function useMyFeedback(enabled = true) {
  const identity = useScopeIdentity();
  const reader = useId();
  const auth = useAuthStore(s => !s.hydrating && !!s.user);
  const scoped = useScopeStore(s => !!s.teamId && !s.selectionRequired);
  const active = enabled && auth && scoped;
  const read = useMemo(() => ({ identity, active }), [identity, active]);
  const queryKey = useScopedKey("my-feedback", { reader, authority: identity.authority, generation: identity.generation });
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!read.active || !read.identity.current() || signal.aborted) throw new DOMException('Context changed', 'AbortError');
      const data = await getMyFeedback();
      if (!read.identity.current() || signal.aborted) throw new DOMException('Context changed', 'AbortError');
      return { read, data };
    },
    enabled: active,
    retry: false,
    staleTime: 0,
  });
  // Never expose a cached bundle on a fresh mount or after leaving and
  // returning to an identity. It supplies child selection as well as content.
  const data: FeedbackBundle | undefined = active && identity.current() && query.data?.read === read ? query.data.data : undefined;
  return { ...query, data, isLoading: active && !data && !query.isError };
}
